/**
 * The end (§4, §15.2 #9, §15.7): the winning team, every role, and the list-style replay: per
 * round, every handshake with both cards, the Bulletin, every Dispatch (true result and Bugged
 * mark), the nominations and the ballots.
 */
import { PlayerChip } from "../components/PlayerChip.tsx";
import { BALLOT_OUTCOMES, cardLabel, outcomeText, roleName, teamName } from "../lib/labels.ts";
import { useGame } from "../lib/store.ts";
import { playerOf, useView } from "../lib/view.ts";
import type { ReplayData, ReplayRound } from "../lib/types.ts";
import { Survey } from "./Survey.tsx";

export function End() {
  const view = useView();
  const replay = useGame((s) => s.replay);
  const reveal = view.public.reveal;
  return (
    <div className="stack" data-testid="end">
      {reveal ? (
        <section className="panel gold center">
          <div className="kicker">The gala is over</div>
          <h1 data-testid="winner">
            {reveal.outcome.winner === "syndicate" ? "The Syndicate wins" : "The Delegates win"}
          </h1>
          <p className="muted">{outcomeText(reveal.outcome)}</p>
          {view.me.team ? (
            <p>{view.me.team === reveal.outcome.winner ? "Your team won." : "Your team lost."}</p>
          ) : null}
        </section>
      ) : null}
      {reveal ? (
        <section className="panel" data-testid="role-reveal">
          <h3>Everyone's role</h3>
          <ul className="list">
            {reveal.players.map((p) => (
              <li key={p.id} data-player={playerOf(view, p.id)?.name}>
                <PlayerChip player={playerOf(view, p.id)} />
                <span className="grow" />
                <span>{roleName(p.role)}</span>
                <span className={p.team === "syndicate" ? "tag" : "tag plain"}>
                  {p.team === "syndicate" ? "Agent" : "Delegate"}
                </span>
              </li>
            ))}
          </ul>
          {reveal.seed ? <p className="faint">Seed: {reveal.seed}</p> : null}
        </section>
      ) : null}
      {reveal ? <Survey /> : null}
      {replay ? <Replay replay={replay} /> : <p className="muted center">Loading the replay…</p>}
    </div>
  );
}

function nameIn(replay: ReplayData, id: string | null | undefined): string {
  if (!id) return "—";
  return replay.players.find((p) => p.id === id)?.name ?? "Someone";
}

export function Replay({ replay, allOpen = false }: { replay: ReplayData; allOpen?: boolean }) {
  return (
    <section className="stack" data-testid="replay">
      <h2>Replay</h2>
      {replay.bluffs.length > 0 ? (
        <p className="muted">Safe bluffs: {replay.bluffs.map(roleName).join(", ")}</p>
      ) : null}
      {replay.rounds.map((r) => (
        <ReplayRoundView key={r.round} replay={replay} round={r} open={allOpen} />
      ))}
    </section>
  );
}

function ReplayRoundView({
  replay,
  round,
  open,
}: {
  replay: ReplayData;
  round: ReplayRound;
  open: boolean;
}) {
  const n = (id: string | null | undefined) => nameIn(replay, id);
  const b = round.bulletin;
  return (
    <details
      className="panel"
      open={open || round.round === replay.rounds.length}
      data-testid={`replay-round-${round.round}`}
    >
      <summary style={{ cursor: "pointer", minHeight: "2.5rem" }}>
        <strong style={{ fontFamily: "var(--serif)", fontSize: "1.15rem" }}>
          Round {round.round}
        </strong>
        <span className="faint">
          {" "}
          · {round.handshakes.length} handshakes
          {b ? ` · ${b.leaks} leaks · ${b.blocked} blocked` : ""}
        </span>
      </summary>
      <h3 style={{ marginTop: "0.75rem" }}>Handshakes</h3>
      {round.handshakes.length === 0 ? <p className="muted">None.</p> : null}
      <ul className="list replay-hs">
        {round.handshakes.map((h) => (
          <li key={h.id} style={{ flexWrap: "wrap" }}>
            <span>
              {n(h.a)} <em className="muted">{h.cardA ? cardLabel(h.cardA) : "—"}</em>
              {h.timedOutA ? <span className="faint"> (timed out)</span> : null}
            </span>
            <span className="faint">↔</span>
            <span>
              {n(h.b)} <em className="muted">{h.cardB ? cardLabel(h.cardB) : "—"}</em>
              {h.timedOutB ? <span className="faint"> (timed out)</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {b ? (
        <>
          <h3>Bulletin</h3>
          <p>
            Leaks {b.leaks} · track {b.leakTrack}/{b.leakLimit} · blocked {b.blocked} · collapsed:{" "}
            {b.deaths.length ? b.deaths.map(n).join(", ") : "no one"} · wallflowers:{" "}
            {b.wallflowersSuppressed
              ? "not listed"
              : b.wallflowers.length
                ? b.wallflowers.map(n).join(", ")
                : "none"}
          </p>
        </>
      ) : null}
      {round.dispatches.length > 0 ? (
        <>
          <h3>Dispatches</h3>
          <ul className="list">
            {round.dispatches.map((d) => (
              <li key={d.player} style={{ display: "block" }}>
                <strong>{n(d.player)}</strong>
                {d.bugged ? (
                  <span className="tag" style={{ marginLeft: "0.4rem" }}>
                    Bugged
                  </span>
                ) : null}
                <div>{d.text}</div>
                {d.bugged || d.text !== d.trueText ? (
                  <div className="faint">Truth: {d.trueText}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {round.nominations.length > 0 ? (
        <>
          <h3>Nominations</h3>
          <p>
            {round.nominations
              .map(
                (x) =>
                  `${n(x.nominee)} (by ${n(x.nominator)}${x.seconder ? `, seconded by ${n(x.seconder)}` : ", no second"})`,
              )
              .join(" · ")}
          </p>
        </>
      ) : null}
      {round.ballots.map((bal) => (
        <div key={bal.number}>
          <h3>{bal.number === 2 ? "Second ballot" : "Ballot"}</h3>
          <p>
            {bal.ballots
              .map(
                (v) =>
                  `${n(v.voter)} → ${v.choice === "none" ? "No exile" : n(v.choice)}${v.gavel ? " (Gavel)" : ""}${v.ghost ? " (Ghost)" : ""}`,
              )
              .join(" · ") || "No ballots."}
          </p>
          <p className="muted">
            {bal.exiled
              ? `${n(bal.exiled)} was exiled (${teamName(replay.players.find((p) => p.id === bal.exiled)?.team)}).`
              : BALLOT_OUTCOMES[bal.outcome]}
          </p>
        </div>
      ))}
      {round.pins.length > 0 ? (
        <p className="faint">
          Syndicate board:{" "}
          {round.pins.map((p) => `${n(p.agent)} pinned ${n(p.target)} (${p.icon})`).join(" · ")}
        </p>
      ) : null}
    </details>
  );
}
